import React, { useEffect, useRef, useState } from 'react';
import { switchRole } from '../services/api';
import { showError } from './ToastNotifications';

// Display names of the four roles (user identities, 2026-10-30).
export const ROLE_LABELS = { administrator: 'Administrator', finance: 'Finance', sales: 'Sales', auditor: 'Auditor (read-only)' };
const roleLabel = (r) => ROLE_LABELS[r] || r;

// Header role switcher for a person who holds several roles: "{nickname} · {Role} ▾"
// opens a list of every role (active one ticked and disabled). Picking another
// asks the server to switch (the api interceptor shows the passkey / code prompt
// it may demand and retries once), then does a FULL reload on the dashboard —
// pages, the bell, task counts and the AI bubble all cache role-scoped state.
// Other open tabs are told through localStorage 'crm-role-switch' (App.js reloads them).
export default function RoleSwitcher({ user }) {
  const [open, setOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const ref = useRef(null);

  // Outside click / touch and Escape close the popover (same pattern as ActionMenu).
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('touchstart', onDoc, { passive: true });
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('touchstart', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const choose = async (r) => {
    if (busyId || r.user_id === user.id) return;
    setBusyId(r.user_id);
    try {
      await switchRole(r.user_id);
      try { localStorage.setItem('crm-role-switch', String(Date.now())); } catch (e) { /* storage blocked: other tabs catch up on their next heartbeat */ }
      window.location.assign('/dashboard');
    } catch (err) {
      setBusyId(null);
      // A cancelled step-up prompt rejects with the original 403 — nothing to add then.
      const code = String(err.response?.data?.code || '');
      if (code === 'TWO_FACTOR_REQUIRED' || code === 'PASSKEY_REQUIRED') return;
      showError(err.response?.data?.error || 'Could not switch role');
    }
  };

  return (
    <div className="role-switcher" ref={ref}>
      <button
        type="button"
        className="app-nav-user app-nav-user-btn role-switcher__btn"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        title="Switch role"
      >
        {user.username} · {roleLabel(user.role)} ▾
      </button>
      {open && (
        <div className="role-switcher__panel" role="menu">
          {user.roles.map((r) => {
            const active = r.user_id === user.id;
            return (
              <button
                key={r.user_id}
                type="button"
                role="menuitem"
                className={`role-switcher__item${active ? ' is-active' : ''}`}
                disabled={active || !!busyId}
                aria-current={active ? 'true' : undefined}
                aria-label={`${r.nickname} — ${roleLabel(r.role)}${active ? ' (current)' : ''}`}
                onClick={() => choose(r)}
              >
                <span className="role-switcher__tick" aria-hidden="true">{active ? '✓' : ''}</span>
                <span className="role-switcher__text">{r.nickname} — {roleLabel(r.role)}</span>
                {busyId === r.user_id && <span className="role-switcher__busy">Switching…</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
